<template>
  <div>
    <div
      v-if="loaded"
      class="top-content-bar">
      <div class="wrapper pt-3 pb-1">
        <validation-observer
          ref="articles-filters-form"
          tag="form"
          class="search-form">

          <template v-if="filtersBtnVisible">
            <f-button
              theme="primary"
              class="block full-width mb-2"
              @click.prevent="$router.push('/artykuly/nowy')">
              {{ $t('articleList.createArticle')}}
            </f-button>
            <f-button
              type="button"
              theme="dark"
              class="block full-width mb-3"
              @click.stop="toggleFilters">
              <template v-if="filtersOpen">
                {{ $t('advisersFinder.collapseFilters')}}
              </template>
              <template v-else>
                {{ $t('advisersFinder.expandFilters')}}
              </template>
            </f-button>
          </template>

          <transition name="slide">
            <div v-if="!filtersBtnVisible || filtersOpen">
              <div class="flex search-form-top">
                <div class="search-form-inputs flex full-width">
                  <f-input-wrapper
                    prependIcon="search"
                    class="search search-field mb-auto full-width">
                    <f-input
                      :placeholder="$t('ui.search')"
                      v-model="filters.search"/>
                  </f-input-wrapper>
                </div>

                <div class="search-form-buttons flex">
                  <f-button
                    theme="primary"
                    class="ml-1 mb-1-ms ml-0-sm"
                    big
                    @click.prevent="validateFiltersForm">
                    {{ $t('ui.search')}}
                  </f-button>

                  <f-button
                    theme="warning"
                    class="ml-1 mb-1-ms"
                    big
                    @click.prevent="clearFilters">
                    {{ $t('advisersFinder.resetFilters')}}
                  </f-button>

                  <f-button
                    v-if="!filtersBtnVisible"
                    theme="dark"
                    class="ml-1"
                    @click.prevent="$router.push('/artykuly/nowy')">
                    {{ $t('articleList.createArticle') }}
                  </f-button>
                </div>
              </div>

              <div class="flex flex-wrap mt-2 mt-3-sm">
                <f-badge
                  v-for="(articleStatus, index) of articleStatuses"
                  :key="'statsus-' +index"
                  class="mr-1 mb-2"
                  :theme="articleStatus.badgeTheme"
                  :checkable="true">
                  <f-checkbox
                    v-model="filters.status[articleStatus.value]">
                    {{ articleStatus.text }}
                  </f-checkbox>
                </f-badge>
              </div>

              <div class="flex flex-align-items-center hidden-from-desktop-small-only mt-2">
                <label for="order" class="mb-0">{{ $t('advisersFinder.sort') }}:</label>
                <v-select
                  class="minimal ml-1 mobile-dropdown-align-left"
                  name="order"
                  :options="orderOptions"
                  v-model="order"
                  :reduce="(item) => { return { column: item.value, direction: item.direction } }"
                  :placeholder="$t('user.selectOrder')"
                  :clearable="false"
                  :searchable="false"
                  label="label">
                </v-select>
              </div>
            </div>
          </transition>
        </validation-observer>
      </div>
    </div>

    <div :class="{'wrapper mt-3': true, 'is-loading': !loaded}">
      <f-list
        ref="articles-list"
        class="articles-list"
        :loaded="loaded"
        :listSettings="articlesListSettings"
        :listItems="articlesFormatted"
        :data-mobile-actions-count="1"
        :itemRoute="'/artykuly/:id/'" />
    </div>

    <template v-if="hasPagination">
      <pagination
        :hasWrapper="true"
        :meta-data="paginationMetaData"
        :elementsCounterLabel="'articles'" />
    </template>

    <div
      v-if="!loaded && !loadError"
      class="loading-spinner-wrapper">
      <div class="loading-spinner pb-3 pt-3">
        <span class="loading-spinner-content">
          {{ $t('ui.loadingDataInProgress') }}&hellip;
        </span>
      </div>
    </div>
    <div class="wrapper mt-3">
      <f-alert
        v-if="loadError"
        visible
        theme="warning">
        <span>
          {{ $t('ui.dataLoadError.dataErrorMess') }} <a href="javascript:window.location.reload();" class="lnk lnk-alert">{{ $t('ui.dataLoadError.resetPageButton') }}</a> {{ $t('ui.dataLoadError.forTryAgain') }}
        </span>
      </f-alert>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';
import ListUtils from '@/utils/ListUtils.js';
import Pagination from '@/components/core/Pagination.vue';
import HasFilters from '@/mixins/HasFilters.vue';
import HasPagination from '@/mixins/HasPagination.vue';
import ArticleStatuses from '@/data/article-statuses.js';

export default {
  name: 'articles-list',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  props: {
    withFilters: {
      type: Boolean
    },
    hasPagination: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    articleStatuses () {
      return ArticleStatuses;
    },
    articlesListSettings () {
      let settingList = [
        {
          title: 'ID',
          variableName: ['id'],
          type: 'String',
          sortable: true,
          sortingEvent: 'articles-sort-toggle'
        },
        {
          title: this.$t('user.status'),
          variableName: ['status'],
          type: 'f-badge'
        },
        {
          title: this.$t('ui.title'),
          variableName: ['title'],
          type: 'String'
        },
        {
          title: this.$t('list.lastModify'),
          variableName: ['updated_at'],
          type: 'f-time-elapsed',
          maxTime: 0
        },
        {
          title: this.$t('articleList.published'),
          variableName: ['article_link'],
          type: 'external-link'
        },
        {
          title: '',
          variableName: ['actionsList'],
          type: 'actions',
          items: [
            {
              type: 'button',
              disabled: false,
              event: 'article-edit',
              label: this.$t('ui.open'),
              name: 'open'
            },
            {
              type: 'button',
              disabled: false,
              label: this.$t('ui.remove'),
              theme: 'danger',
              name: 'delete',
              event: 'article-delete',
            }
          ]
        }
      ];

      return settingList;
    },
    articlesFormatted () {
      let articlesList = this.articles;

      if (!articlesList.length) {
        return [];
      }

      return articlesList.map(item => {
        item.actionsList = ['open'];
        if (item.can_delete) {
          item.actionsList.push('delete');
        }

        let itemStatus = this.articleStatuses.find(element => element.value === item.status);
        item.status = {
          status: itemStatus.badgeTheme,
          text: itemStatus.text
        }

        item.article_link = {
          url: item.blog_url,
          text: this.$t('articleList.linkToArticle')
        }

        return item;
      });
    },
  },
  data() {
    return {
      articles: [],
      filters: {
        search: '',
        status: {}
      },
      filtersOpen: false,
      filtersBtnVisible: false,
      loaded: false,
      loadError: false,
      removeInProgress: false,
      order: {
        column: 'id',
        direction: 'DESC'
      },
      orderOptions: [
        {
          label: this.$t('list.fromLatest'),
          value: 'id',
          direction: 'DESC'
        },
        {
          label: this.$t('list.fromOldest'),
          value: 'id',
          direction: 'ASC'
        }
      ]
    }
  },
  mounted() {
    this.$bus.$on('article-delete', this.removeArticle);
    this.$bus.$on('article-edit', this.editArticle);
    this.$bus.$on('articles-sort-toggle', this.toggleSort);
    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let endpointParams = {};

      if (this.withFilters) {
        let where = {};

        if (this.filters.search) {
          where.search = this.filters.search;
        }

        if (Object.keys(this.filters.status).filter(item => this.filters.status[item] === true)) {
          where.status = Object.keys(this.filters.status).filter(item => this.filters.status[item] === true);
        }

        endpointParams.where = where;
        endpointParams.order = this.order;
      }

      let enpointUrl = '/api/adviser/adviser/articles/items'

      if (this.hasPagination) {
        enpointUrl += '?page=' + this.currentPage
      }

      ListUtils.loadItemsData(this, {
        method: 'get',
        endpoint: enpointUrl,
        listField: 'articles',
        pagination: this.hasPagination,
        params: endpointParams,
      });
    },
    toggleFilters () {
      Vue.set(this, 'filtersOpen', !this.filtersOpen);
    },
    removeArticle (article) {
      this.removeInProgress = true;
      let articleId = typeof article === 'object' ? article.id : article;

      ListUtils.removeItem(this, {
        endpointBase: '/api/adviser/adviser/articles/delete/',
        id: articleId,
        successTitle: this.$t('articleList.deleteArticle'),
        successText: this.$t('articleList.deleteSelectArticle'),
        confirmTitle: this.$t('articleList.doYouWantDeleteArticle'),
        confirmText: this.$t('ui.thisOperationCannotUndone'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.$t('articleList.deleteArticleError'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    editArticle (article) {
      this.$router.push('/artykuly/' + article.id);
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['articles-filters-form'], this.debouncedLoadFilteredData);
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        status: {}
      };

      this.order = {
        column: 'id',
        direction: 'DESC'
      }

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    toggleSort (param) {
      this.order.column = param.columnName[0];
      this.order.direction = this.order.direction === 'ASC' ? 'DESC' : 'ASC';
      this.loadFilteredData();
    },
  },
  beforeDestroy () {
    this.$bus.$off('article-delete', this.removeArticle);
    this.$bus.$off('article-edit', this.editArticle);
    this.$bus.$on('articles-sort-toggle', this.toggleSort);
  }
}
</script>
